import CloseSVG from '@public/icons/close-btn.svg';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import Pagination from '@/shared/components/shared/Pagination';
import { useMounted } from '@/shared/hooks/use-mounted';
import { tp } from '@/shared/lib/typograf';
import Typografed from '../../shared/Typografed';
import { usePavilionsValue } from '../atoms/pavilionsData';
import { AppPavilionDataMap } from '../utils/getStandsDataMap';
import { PAVILION_MAP_SLUG, sortPavilionRooms } from '../utils/sortPavilionRooms';
import { useActiveRoom, useSetActiveRoom } from '../utils/use-active-room';

type Props = React.HTMLAttributes<HTMLElement> & {
    slug: string;
};

const MapDescription = ({ slug, ...props }: Props) => {
    const setActiveRoomInStore = useSetActiveRoom();
    const activeRoom = useActiveRoom();

    const { pavilionData, pavilionDataLoading, pavilionDataError } = usePavilionsValue();

    const data = useMemo(() => {
        if (!pavilionData.data?.[slug]) return [];
        const schemaData = pavilionData.data[slug];
        if (!schemaData) return [];

        const rooms = Object.values(schemaData).filter(
            (item): item is AppPavilionDataMap => item !== null && item !== undefined,
        );

        return slug === PAVILION_MAP_SLUG ? sortPavilionRooms(rooms) : rooms;
    }, [pavilionData.data, slug]);

    const bookingStands = useMemo(() => {
        const filtered = data.filter((stand) => stand.booking);
        return slug === PAVILION_MAP_SLUG ? sortPavilionRooms(filtered) : filtered;
    }, [data, slug]);

    const sliderRef = useRef<SwiperRef>(null);
    const isMounted = useMounted();
    const [loop, setLoop] = useState(false);
    const [currentRealIndex, setCurrentRealIndex] = useState(0);
    const [lastRealIndex, setLastRealIndex] = useState(0);
    const isExternalChange = useRef(false);

    useEffect(() => {
        setLoop(bookingStands.length > 1);
    }, [bookingStands.length]);

    const handleSlideChange = (swiper: any) => {
        const newRealIndex = swiper.realIndex;

        if (isExternalChange.current) {
            isExternalChange.current = false;
            setLastRealIndex(currentRealIndex);
            setCurrentRealIndex(newRealIndex);
            return;
        }

        const newActiveStand = bookingStands[newRealIndex];
        if (newActiveStand && newActiveStand.slug !== activeRoom?.slug) {
            setActiveRoomInStore(newActiveStand);
        }

        setLastRealIndex(currentRealIndex);
        setCurrentRealIndex(newRealIndex);
    };

    const activeIndex = useMemo(() => {
        if (!activeRoom) return 0;
        return bookingStands.findIndex((stand) => stand.slug === activeRoom.slug);
    }, [activeRoom, bookingStands]);

    // Синхронизация слайдера с внешними изменениями
    useEffect(() => {
        if (!sliderRef.current?.swiper || activeIndex === -1) return;

        if (sliderRef.current.swiper.realIndex !== activeIndex) {
            isExternalChange.current = true;
            sliderRef.current.swiper.slideTo(activeIndex);
        }
    }, [activeIndex]);

    const handleClose = () => {
        setActiveRoomInStore(null);
    };

    if (pavilionDataLoading) {
        return <div>Загрузка...</div>;
    }

    if (pavilionDataError) {
        return <div>Ошибка: {pavilionDataError}</div>;
    }

    return (
        <div className="map-description__wrapper" {...props}>
            <button className="close-description menu-popup-close-btn" aria-label="Закрыть" onClick={handleClose}>
                <CloseSVG />
            </button>

            {isMounted && activeRoom && bookingStands.length > 0 && (
                <>
                    <Swiper
                        ref={sliderRef}
                        modules={[Navigation]}
                        centeredSlides
                        speed={800}
                        initialSlide={activeIndex}
                        spaceBetween={60}
                        slidesPerView={1}
                        navigation={{
                            prevEl: '.slider-prev-btn',
                            nextEl: '.slider-next-btn',
                        }}
                        onSlideChange={handleSlideChange}
                    >
                        {bookingStands.map((bookingStand) => (
                            <SwiperSlide key={bookingStand.slug} className="map-description__slide">
                                {bookingStand.booking?.company && (
                                    <>
                                        <div className="map-description__header">
                                            <div className="map-description__title text-m">
                                                <span>
                                                    <Typografed>{bookingStand.booking.company.name}</Typografed>
                                                </span>
                                                {bookingStand.space && (
                                                    <div className="map-description__space text-xs">
                                                        {bookingStand.space} м²
                                                    </div>
                                                )}
                                            </div>

                                            <div className="map-description__stand text-xs">
                                                Стенд{' '}
                                                <span className="map-description__stand-slug">{bookingStand.slug}</span>
                                            </div>
                                        </div>
                                        <div className="map-description__content text-s">
                                            {bookingStand.booking.company.description && (
                                                <div className="map-description__content-text">
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: tp(bookingStand.booking.company.description),
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            {bookingStand.booking.company.url && (
                                                <div className="map-description__content-link text-opacity">
                                                    <a
                                                        href={bookingStand.booking.company.url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {bookingStand.booking.company.url}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {bookingStands.length > 1 && (
                        <Pagination
                            numSlides={bookingStands.length}
                            currentRealIndex={currentRealIndex}
                            lastRealIndex={lastRealIndex}
                            prevElClass="slider-prev-btn"
                            nextElClass="slider-next-btn"
                            wrapperClass="pagination-wrapper wrapper"
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default memo(MapDescription);
